import { Badge, Button, message, TableColumnsType } from 'antd';
import ScrollTable, { ScrollTableProps } from 'components/ScrollTable';
import StatusSwitch from 'components/StatusSwitch';
import useModal from 'hooks/useModal';
import { langRegionMap, useGetRegionLabel } from './share';
import dayjs from 'dayjs';
import { deleteTemplate, updateTemplateGroup } from './service';
import StatusButton from 'components/StatusButton';
import { getLength, value2editorValue } from './WhatsAppEditor/ContentEditor';
import styled from '@emotion/styled';
import { Content } from 'components/WhatsAppTemplatePreview';
import PreviewModal from './PreviewModal';

type RT = Aide.WhatsAppTemplateGroup;

function TemplateTable({
  wabaList,
  onUpdate,
  onEdit,
  onCopy,
  ...otherProps
}: ScrollTableProps<RT> & {
  wabaList: Aide.Waba[];
  onUpdate: () => Promise<any>;
  onEdit: (record: RT) => void;
  onCopy: (record: RT) => void;
}) {
  const { t, getRegionLabel } = useGetRegionLabel();

  const { meta, visible, show, hide } = useModal<RT>();

  const previewModal = meta && (
    <PreviewModal visible={visible} onCancel={() => hide()} template={meta.template[0]} />
  );

  const update = (updatePromise: Promise<any>) =>
    updatePromise
      .then(onUpdate)
      .then(() => {
        message.success(t('phrase.保存成功'));
      })
      .catch(() => {
        message.error(t('phrase.保存失败，请稍后再试'));
      });

  const columns: TableColumnsType<RT> = [
    {
      title: t('模版名称'),
      dataIndex: 'name',
      width: 180,
      fixed: 'left',
    },
    {
      title: t('WABA账号'),
      width: 180,
      render: (_, { template }) =>
        wabaList.find(({ wabaId }) => wabaId === template[0].wabaId)?.phone || '-',
    },
    {
      title: t('phrase.分组'),
      dataIndex: 'group',
      width: 160,
    },
    {
      title: t('phrase.内容'),
      width: 280,
      render: (_, record) => {
        const { content, translateContent } = record.template[0];
        return (
          <Content
            contents={value2editorValue(getLength(content) ? content : translateContent)}
            placeholder=""
            style={{ marginBottom: 0 }}
          />
        );
      },
    },
    {
      title: t('站点'),
      width: 180,
      render: (_, { template }) => template.map(({ lang }) => langRegionMap[lang]).join(' | '),
    },
    {
      title: t('更新时间'),
      width: 180,
      render: (_, { template }) => dayjs(Number(template[0].updateAt)).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('状态'),
      width: 180,
      render: (_, { status, template }) => {
        if (!status) {
          return <Badge status="warning" text={t('已停用')} />;
        }
        const rejectedList = template.filter(({ state }) => state === 'REJECTED');
        if (rejectedList.length > 0) {
          return (
            <div>
              <Badge status="error" text={t('已驳回')} />
              {rejectedList.map(({ lang, rejectedReason }) => {
                if (!rejectedReason) {
                  return null;
                }
                const regionLabel = getRegionLabel(langRegionMap[lang]);
                return (
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--ant-error-color)',
                    }}
                  >
                    {regionLabel}：{rejectedReason}
                  </div>
                );
              })}
            </div>
          );
        }
        const disabledList = template.filter(({ state }) => state === 'DISABLED');
        if (disabledList.length > 0) {
          return (
            <div>
              <Badge status="default" text={t('已禁用')} />
              {disabledList.map(({ lang, rejectedReason }) => {
                const regionLabel = getRegionLabel(langRegionMap[lang]);
                return (
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--ant-error-color)',
                    }}
                  >
                    {regionLabel}：{rejectedReason}
                  </div>
                );
              })}
            </div>
          );
        }
        if (template.some(({ state }) => state === 'PENDING' || state === 'PENDING_REVIEW')) {
          return <Badge status="processing" text={t('审核中')} />;
        }
        if (template.every(({ state }) => state === 'APPROVED')) {
          return <Badge status="success" text={t('已通过')} />;
        }
        return '-';
      },
    },
    {
      title: t('是否展示'),
      width: 160,
      render: (_, { show, template, ...others }) => {
        return (
          <StatusSwitch
            checked={show}
            onChange={(checked) => {
              return update(
                updateTemplateGroup({
                  ...others,
                  wabaId: template[0].wabaId,
                  show: checked,
                }),
              );
            }}
          />
        );
      },
    },
    {
      title: t('操作'),
      width: 320,
      fixed: 'right',
      render: (_, record) => {
        const { template, status, ...others } = record;
        const wabaId = template[0].wabaId;
        return (
          <Operation>
            {status && template.every(({ state }) => state === 'APPROVED') && (
              <StatusButton
                type="link"
                popConfirmProps={{
                  title: t('你确定要停用吗？'),
                }}
                onClick={() => {
                  return update(
                    updateTemplateGroup({
                      ...others,
                      wabaId,
                      status: false,
                    }),
                  );
                }}
              >
                {t('account.disable')}
              </StatusButton>
            )}
            {false && !status && (
              <StatusButton
                type="link"
                popConfirmProps={{
                  title: t('upload.你确定要删除吗'),
                }}
                onClick={() => {
                  return deleteTemplate({ name: record.name, wabaId })
                    .then(onUpdate)
                    .then(() => {
                      message.success(t('phrase.删除成功'));
                    })
                    .catch(() => {
                      message.error(t('phrase.删除失败，请稍后再试'));
                    });
                }}
              >
                {t('phrase.删除')}
              </StatusButton>
            )}
            <Button type="link" onClick={() => onCopy(record)}>
              {t('aide.复制')}
            </Button>
            {template.every(({ state }) => state !== 'PENDING' && state !== 'PENDING_REVIEW') && (
              <Button type="link" onClick={() => onEdit(record)}>
                {t('编辑')}
              </Button>
            )}
            <Button type="link" onClick={() => show(record)}>
              {t('trade.预览')}
            </Button>
            {!status && (
              <StatusButton
                type="link"
                onClick={() => {
                  return update(
                    updateTemplateGroup({
                      ...others,
                      wabaId,
                      status: true,
                    }),
                  );
                }}
              >
                {t('ViewsChat.启用')}
              </StatusButton>
            )}
          </Operation>
        );
      },
    },
  ];

  return (
    <>
      <ScrollTable
        rowKey="name"
        columns={columns}
        pagination={{
          showTotal: (x) => t('feedback.共x项', { x }),
        }}
        {...otherProps}
      />
      {previewModal}
    </>
  );
}

const Operation = styled.div`
  > button:first-of-type {
    padding-left: 0;
  }
`;

export default TemplateTable;
